import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Save,
  FileText,
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import SystemPickerByUuid from './SystemPickerByUuid';

const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
const CHIP_GREEN = `${CHIP_BASE} bg-green-100 text-green-700`;
const CHIP_GRAY = `${CHIP_BASE} bg-gray-100 text-gray-600`;
const CHIP_BLUE = `${CHIP_BASE} bg-blue-100 text-blue-700`;
const CHIP_AMBER = `${CHIP_BASE} bg-amber-100 text-amber-800`;

const INPUT_BASE =
  'w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-700 disabled:cursor-not-allowed';

const SECTION_HEADER = 'text-xs font-bold tracking-wide uppercase text-slate-500 border-b border-gray-200 pb-2';
const FIELD_LABEL = 'block text-sm font-medium text-gray-700 mb-1';

function MetaItem({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <div className="text-sm font-medium text-slate-700 truncate">{children}</div>
    </div>
  );
}

function formatTimestamp(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '-';
  }
}

function emptyForm() {
  return {
    reportId: '',
    name: '',
    isReport: false,
    keyReport: false,
    standard: true,
    isActive: true,
    systemIds: [],
  };
}

function hydrateForm(report) {
  if (!report) return emptyForm();
  return {
    reportId: report.reportId || '',
    name: report.name || '',
    isReport: String(report.docType || '').toLowerCase() === 'report',
    keyReport: !!report.keyReport,
    standard: !!report.standard,
    isActive: report.isActive !== false,
    systemIds: Array.isArray(report.systems) ? report.systems.map((s) => s.id).filter(Boolean) : [],
  };
}

function ReportDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === '1');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/api/reports/${id}`);
        if (cancelled) return;
        setReport(res.data || null);
        setForm(hydrateForm(res.data || null));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError('Report not found in your catalog.');
        } else {
          console.error('Error loading report:', err);
          setError(err?.response?.data?.error || 'Failed to load report');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const goBack = () => navigate('/sox-reports');
  const startEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };
  const cancelEdit = () => {
    setForm(hydrateForm(report));
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!report?.id || saving) return;
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      setSaveError('Name is required.');
      return;
    }
    if (trimmedName.length > 255) {
      setSaveError('Name must be 255 characters or fewer');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        reportId: form.reportId.trim(),
        name: trimmedName,
        isReport: form.isReport,
        keyReport: form.keyReport,
        standard: form.standard,
        isActive: form.isActive,
        systemIds: form.systemIds,
      };
      const res = await axiosInstance.put(`/api/reports/${report.id}`, payload);
      const updated = res.data || {};
      setReport(updated);
      setForm(hydrateForm(updated));
      setIsEditing(false);
    } catch (err) {
      console.error('Save report failed:', err);
      setSaveError(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to save report'));
    } finally {
      setSaving(false);
    }
  };

  const createdLabel = useMemo(() => formatTimestamp(report?.createdAt), [report?.createdAt]);
  const updatedLabel = useMemo(() => formatTimestamp(report?.updatedAt), [report?.updatedAt]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-500">Loading report…</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-700 mb-4">{error || 'Report not found.'}</p>
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to reports
          </button>
        </div>
      </div>
    );
  }

  const linkedSystems = Array.isArray(report.systems) ? report.systems : [];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Sticky top header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goBack}
            className="shrink-0 p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Back to reports"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 truncate">
                {isEditing ? 'Edit Document & Report' : 'Document & Report'}
              </h1>
              <span className="text-sm font-medium text-gray-500 truncate">{report.name}</span>
            </div>
            <p className="text-sm text-gray-500 truncate">
              {isEditing
                ? 'Update the catalog record. Document ID stays as a stable identifier.'
                : 'Catalog record details and linked systems.'}
            </p>
          </div>
          {!isEditing && (
            <button
              type="button"
              onClick={startEdit}
              className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={goBack}
            className="shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
        {/* Meta strip */}
        <div className="bg-slate-50 border border-gray-200 rounded-lg px-4 py-3 mb-4 flex flex-wrap items-start gap-x-8 gap-y-3">
          <MetaItem label="Document ID">
            <span className="font-mono text-blue-700">{report.reportId || '-'}</span>
          </MetaItem>
          <MetaItem label="Status">
            <span className={report.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
              {report.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </MetaItem>
          <MetaItem label="Created">{createdLabel}</MetaItem>
          <MetaItem label="Last Updated">{updatedLabel}</MetaItem>
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{saveError}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Summary card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-4">
            <h2 className={SECTION_HEADER}>Summary</h2>

            <div>
              <label className={FIELD_LABEL}>Document ID</label>
              {/* The report ID is assigned by the trg_reports_set_report_id
                  trigger on insert and serves as the stable code in
                  matrices and working papers. Editing it would break
                  every downstream reference, so it stays read-only on
                  both view and edit. We still keep form.reportId in
                  state and ship it on save so the PUT body remains
                  symmetric — backend treats an unchanged value as a
                  no-op update. */}
              <p className="text-sm font-mono text-blue-700">{report.reportId || '—'}</p>
            </div>

            <div>
              <label className={FIELD_LABEL}>
                Name {isEditing && <span className="text-red-600">*</span>}
              </label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={INPUT_BASE}
                  />
                  {form.name.length > 255 ? (
                    <p className="text-xs text-red-600 mt-1.5">Name must be 255 characters or fewer</p>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-gray-900">{report.name}</p>
              )}
            </div>
          </div>

          {/* Classification card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-5">
            <h2 className={SECTION_HEADER}>Classification</h2>

            <div>
              <label className={FIELD_LABEL}>Report</label>
              {isEditing ? (
                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isReport}
                    onChange={(e) => setForm({ ...form, isReport: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Consider this document as report</span>
                </label>
              ) : (
                <span className={form.isReport ? CHIP_GREEN : CHIP_GRAY}>
                  {form.isReport ? 'Yes' : 'No'}
                </span>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Scope</label>
              {isEditing ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-key"
                      checked={form.keyReport}
                      onChange={() => setForm({ ...form, keyReport: true })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Key
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-key"
                      checked={!form.keyReport}
                      onChange={() => setForm({ ...form, keyReport: false })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Non-Key
                  </label>
                </div>
              ) : (
                <span className={form.keyReport ? CHIP_GREEN : CHIP_GRAY}>
                  {form.keyReport ? 'Key' : 'Non-Key'}
                </span>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Type</label>
              {isEditing ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-standard"
                      checked={form.standard}
                      onChange={() => setForm({ ...form, standard: true })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Standard
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-standard"
                      checked={!form.standard}
                      onChange={() => setForm({ ...form, standard: false })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Custom
                  </label>
                </div>
              ) : (
                <span className={form.standard ? CHIP_BLUE : CHIP_AMBER}>
                  {form.standard ? 'Standard' : 'Custom'}
                </span>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Active in catalog</label>
              {isEditing ? (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  Active
                </label>
              ) : (
                <span className={form.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
                  {form.isActive === false ? 'Inactive' : 'Active'}
                </span>
              )}
            </div>
          </div>

          {/* Linked systems card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-3">
            <h2 className={SECTION_HEADER}>Linked systems</h2>
            {isEditing ? (
              <>
                <p className="text-xs text-gray-500">
                  Pick the systems that produce or feed this report. The full active link set is replaced on save.
                </p>
                <SystemPickerByUuid
                  value={form.systemIds}
                  onChange={(next) => setForm({ ...form, systemIds: next })}
                />
              </>
            ) : linkedSystems.length === 0 ? (
              <p className="text-sm text-gray-500">No systems linked to this report yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
                {linkedSystems.map((s) => (
                  <li key={s.id} className="px-3 py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                      {s.systemId && (
                        <p className="text-xs text-gray-500 font-mono truncate">{s.systemId}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {isEditing && (
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportDetail;
